import { useCallback, useEffect, useRef, useState } from "react";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";

interface CacheJobResult {
  data?: {
    cache_url?: string;
  };
}

interface UseCacheUrlResult {
  /** The cache to show now: the fresh one once generated, until then the one the run already had. */
  cacheUrl: string | null;
  /** True only while there is nothing to show yet. A stored cache shown during a refresh is not loading. */
  loading: boolean;
  /** True while a fresh cache is generated behind the stored one in `cacheUrl`. */
  refreshing: boolean;
  /** Why the fresh cache could not be generated, while the stored one stays on screen. */
  refreshError: string | null;
  error: string | null;
  /**
   * True when the last failure was a candle-completeness one the operator can override. The backend
   * ends every such message with "...allow_incomplete_candles...", so its presence is the signal that
   * retrying with the flag would un-stick this cache.
   */
  canAllowIncompleteCandles: boolean;
  /** Re-request the cache with allow_incomplete_candles=true, publishing despite the candle gap. */
  retryAllowingIncompleteCandles: () => void;
}

const POLL_INTERVAL_MS = 1000;
const POLL_DEADLINE_MS = 45_000;

/** Poll a cache-generation job until it completes, fails, or is cancelled. Checks at once, then every second. */
export async function pollCacheJob(jobId: number, cancelled: () => boolean): Promise<string> {
  const deadline = Date.now() + POLL_DEADLINE_MS;
  for (;;) {
    if (cancelled()) throw new Error("cancelled");
    const job = await tdApi.getJob(jobId);
    if (cancelled()) throw new Error("cancelled");
    if (job?.status_name === "completed") {
      const url = (job.result as CacheJobResult)?.data?.cache_url;
      if (url) return url;
      throw new Error("Job completed but no cache_url in result");
    }
    if (job?.status_name === "failed") {
      throw new Error(job.error_message || "Cache job failed");
    }
    if (job?.status_name === "cancelled") {
      throw new Error("Cache job was cancelled");
    }
    if (Date.now() >= deadline) throw new Error("Cache generation timed out");
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

export const useCacheUrl = (run: T.ApiRun, type: "backtest" | "execution"): UseCacheUrlResult => {
  const [cacheUrl, setCacheUrl] = useState<string | null>(null);
  // True until the current cache request ends. It shows as loading or refreshing depending on cacheUrl.
  const [pending, setPending] = useState<boolean>(true);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canAllowIncompleteCandles, setCanAllowIncompleteCandles] = useState<boolean>(false);

  // Detect group synthetic runs via explicit flag set by ExecutionGroupDetailPage
  const isGroupRun = "_is_group_synthetic" in run && !!(run as any)._is_group_synthetic;

  // Identity of the run this hook currently serves. Every request (the mount fetch AND an operator
  // retry) captures the key of the run it was started for, then writes state only while that key is
  // still the active one. This is what makes a retry safe: the operator can open the confirm dialog on
  // run A, navigate to run B, and confirm. The retry closure still holds A's key, sees B is now active,
  // and drops itself instead of writing A's cache into B. A plain shared flag or a call-time counter
  // could not tell A's retry from B's, since the retry resolves long after navigation.
  const runKey = `${run.id}:${String(run.end_ts)}:${String(run.cache_url)}:${type}`;
  const activeRunKeyRef = useRef("");

  // Executions and groups — RUNNING OR ENDED — always ask the API.
  //
  // Previously an ended run served run.cache_url directly and never called the route. That made the
  // server's freshness check unreachable from the UI (a stale cache kept being served), and it left an
  // ended execution whose only cache job had failed with NO metrics at all, forever: nothing retried,
  // and the page just said "No cache data available".
  //
  // Asking is cheap: the route returns a NULL BODY when the cache is genuinely fresh, so a valid cache
  // costs one request and we keep using the stored URL. A stale one now returns a job and regenerates.
  const ensureCache = useCallback(
    async (allowIncompleteCandles: boolean) => {
      const myRunKey = runKey;
      const isStale = () => activeRunKeyRef.current !== myRunKey;
      if (isStale()) return;
      try {
        // The stored cache goes on screen at once and the fresh one replaces it when ready, so the chart
        // never waits on the queue and the rebuild. Only a run with no stored cache shows as loading.
        setCacheUrl(run.cache_url || null);
        setPending(true);
        setRefreshError(null);
        setError(null);
        setCanAllowIncompleteCandles(false);

        const opts = allowIncompleteCandles ? { allow_incomplete_candles: true } : undefined;
        const cacheJob = isGroupRun
          ? await tdApi.postExecutionGroupCache(run.id, opts)
          : await tdApi.getExecutionCache(run.id, opts);
        if (isStale()) return;

        // Null body = "your cache is already fresh". Keep the one we have — only re-fetch if this run
        // object somehow has no URL to keep.
        if (!cacheJob?.id) {
          const existingUrl =
            run.cache_url ??
            (isGroupRun ? (await tdApi.getExecutionGroup(run.id))?.cache_url : undefined);
          if (isStale()) return;
          if (!existingUrl) {
            throw new Error("No cache job returned and no existing cache URL");
          }
          setCacheUrl(existingUrl);
          setPending(false);
          return;
        }

        const url = await pollCacheJob(cacheJob.id, isStale);
        if (isStale()) return;
        setCacheUrl(url);
        setError(null);
        setPending(false);
      } catch (err) {
        if (isStale()) return;

        // A regeneration failure must not DESTROY a chart the user could otherwise see. The run may still
        // have a perfectly readable cache — stale only by timestamp, or one the backend has told us it is
        // in cooldown on. Falling back to it is strictly better than a blank panel, so show it and surface
        // the failure alongside rather than instead.
        const message = err instanceof Error ? err.message : String(err);
        // The override only makes sense for a candle-completeness failure, and it must not be re-offered
        // after the operator already retried with the flag (a still-failing overridden run is a real error).
        setCanAllowIncompleteCandles(
          !allowIncompleteCandles && message.includes("allow_incomplete_candles"),
        );
        if (run.cache_url) {
          setCacheUrl(run.cache_url);
          setRefreshError(message);
          setError(null);
        } else {
          setError(message);
        }
        setPending(false);
      }
    },
    [runKey, isGroupRun, run.id, run.cache_url],
  );

  useEffect(() => {
    // This run is now the active one; any earlier request still in flight will see its own key is no
    // longer active and discard its result. Cleanup blanks the key so a request that resolves after a
    // full unmount (no next run) also drops out.
    activeRunKeyRef.current = runKey;

    // Backtests have no cache-generation route — their cache_url is written when the backtest runs.
    if (type === "backtest") {
      setCacheUrl(run.cache_url || null);
      setPending(false);
      setRefreshError(null);
      setCanAllowIncompleteCandles(false);
      if (!run.cache_url) {
        setError("No cache data available");
      }
      return () => {
        activeRunKeyRef.current = "";
      };
    }

    ensureCache(false);

    return () => {
      activeRunKeyRef.current = "";
    };
    // runKey already folds in run.id/end_ts/cache_url/type; run.cache_url is listed too because the
    // backtest branch reads it directly and the deps linter cannot see through the composed key.
  }, [runKey, type, run.cache_url, ensureCache]);

  const retryAllowingIncompleteCandles = useCallback(() => {
    void ensureCache(true);
  }, [ensureCache]);

  return {
    cacheUrl,
    loading: pending && !cacheUrl,
    refreshing: pending && !!cacheUrl,
    refreshError,
    error,
    canAllowIncompleteCandles,
    retryAllowingIncompleteCandles,
  };
};
